import { useState } from 'react';
import { Notice } from '../../components/ui/Notice';
import { useProjects } from '../../projects/ProjectsProvider';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { PromoteBody, SpaceItem } from './spaces-api';

/**
 * PROMOTE A TOPIC INTO A TICKET.
 *
 * 🔴 A project and a type are REQUIRED because a ticket cannot exist without them — `POST /tickets`
 * takes `projectId` and `type`, and the initial status comes from the project's status set. ⛔ There
 * is no status picker here for that reason, and there is no "promote into the current project"
 * shortcut: a space is not scoped to a project, so there is no current one to assume.
 *
 * ⭐ THE TYPES COME FROM THE VOCABULARY, not from a hard-coded list. `type_def` is editable in
 * Settings without a deploy; a literal `['story','bug','task']` here would be right until somebody
 * adds one, then quietly wrong.
 *
 * ⚠️ SUB-TASK TYPES ARE EXCLUDED. db/009 requires a sub-task to have a parent, and a promoted topic
 * has none — offering one would build a request the API must refuse.
 */
export function PromoteDialog({
  item,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  item: SpaceItem;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (body: PromoteBody) => void;
}) {
  const projects = useProjects();
  const vocab = useVocabulary();

  const types = vocab.types.filter((type) => type.is_active && !type.is_subtask);
  const priorities = vocab.priorities.filter((priority) => priority.is_active);

  const [projectId, setProjectId] = useState<string>('');
  /**
   * ⭐ EMPTY MEANS "let the API decide". `type` and `priority` are OPTIONAL on the wire and the API
   * defaults them to `task` and `medium`. ⛔ Pre-selecting the first vocabulary entry would send a
   * deliberate-looking choice nobody made — and `types[0]` is whatever sorts first, which is not the
   * same thing as a sensible default.
   */
  const [type, setType] = useState('');
  const [priority, setPriority] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Promote "${item.title}" to a ticket`}
        className="w-full max-w-lg space-y-3 rounded-t-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4 sm:rounded-lg"
      >
        <h2 className="text-base font-semibold">Promote to ticket</h2>
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          “{item.title}” becomes a real ticket — with a key, a status, a history and a place on the
          board. ⭐ The topic stays here and links to it; it is not moved or deleted.
        </p>
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          ⭐ The note becomes the ticket's description and{' '}
          {item.owner ? <strong>{item.owner}</strong> : 'the item’s owner'} becomes its assignee —
          losing that link is the commonest complaint about promotion features.{' '}
          {item.due_on ? 'The due date becomes the end date. ' : ''}⛔ Only an active member of the
          target project may promote into it, and the API refuses a second promotion rather than
          orphaning the first ticket.
        </p>

        <div>
          <label className="block text-xs font-semibold" htmlFor="promote-project">
            Project
          </label>
          <select
            id="promote-project"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-2 text-sm"
          >
            <option value="">Choose a project…</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.key} — {project.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            A space is not scoped to a project, so there is nothing to default to.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold" htmlFor="promote-type">
              Type
            </label>
            <select
              id="promote-type"
              value={type}
              onChange={(event) => setType(event.target.value)}
              className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-2 text-sm"
            >
              <option value="">Task — the API's default</option>
              {types.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Sub-task types are excluded — db/009 requires a parent and a promoted topic has none.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold" htmlFor="promote-priority">
              Priority
            </label>
            <select
              id="promote-priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
              className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-2 text-sm"
            >
              {/* ⭐ "the API's default" is a real option — omitting the key is not the same as
                  guessing `medium`, and only the API knows what its default is. */}
              <option value="">Use the API's default</option>
              {priorities.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {vocab.types.length === 0 ? (
          <Notice tone="warning" title="The ticket types could not be loaded">
            <p>
              Without them there is no honest list to choose from — ⛔ a hard-coded one would be right
              until somebody adds a type in Settings.
            </p>
          </Notice>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm font-semibold text-red-700 dark:text-red-300">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 rounded border border-[color:var(--color-line)] px-3 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            /* ⭐ Only the PROJECT is required — the API has no default for it and the item does not
               belong to one. Type and priority are genuinely optional. */
            disabled={busy || !projectId}
            onClick={() =>
              onConfirm({
                projectId,
                ...(type ? { type } : {}),
                ...(priority ? { priority } : {}),
              })
            }
            className="h-9 rounded bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
          >
            {busy ? 'Promoting…' : 'Promote'}
          </button>
        </div>
      </div>
    </div>
  );
}
